import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";
import EmojiPicker, { type EmojiClickData } from "emoji-picker-react";
import { useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";

import {
  AttachedDocumentItem,
  AttachedMediaItem,
  AttachedMediaPreview,
  CharacterCountText,
  EmojiPickerPopoverContainer,
  RemoveMediaButton,
  StatsActionsGroup,
  StatsMetaGroup,
  SmsSegmentCountText,
  SmsStatsBar,
} from "./CreateMessageField.styles";
import { INSERT_TEXT_AT_CURSOR_COMMAND } from "./insertTextAtCursor";
import {
  CreateMessageShell,
  CreateMessageStack,
  MessageEditorScrollArea,
  MessageInputContainer,
  SurfaceCard,
} from "./layout.styles";
import { LexicalEditorContent } from "./LexicalMessageEditor";
import type { TextComposerDerivedOutput } from "../channel/channelDerivation";
import {
  RecipientTopBarChannelBadge,
  RecipientTopBarToggle,
  RecipientTopBarToggleButton,
} from "../channel/channelTopBar.styles";
import { isDocumentMedia, vcardDisplayName } from "../channel/mmsConstraints";
import type { TextComposerAuthoringState } from "../editor/authoringState";
import type { TextComposerExtension } from "../editor/extensionContract";
import type { MessageDraftMedia } from "../editor/messageDraftStore";
import {
  useMessageDraftExtensions,
  useMessageDraftStore,
  useMessageDraftStoreId,
} from "../editor/messageDraftStoreFactory";

import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal";
import {
  InfoOutlinedIcon as InfoOutlined,
  LinkIcon,
  LinkOffIcon,
  MoodIcon,
} from "@/icons";

const HEX_BAR_URL = "https://cdn.queen.one/connect/ui/hex-bar.png";

function getChannelColor(
  theme: Theme,
  channel:
    | TextComposerDerivedOutput["primaryChannel"]
    | NonNullable<TextComposerDerivedOutput["fallbackChannel"]>,
) {
  if (channel === "RCS") {
    return theme.palette.success.main;
  }
  if (channel === "MMS") {
    return theme.palette.primary.main;
  }
  if (channel === "INVALID") {
    return theme.palette.text.secondary;
  }
  return theme.palette.info.main;
}

function isVideoMimeType(mimeType: string) {
  return mimeType.startsWith("video/");
}

export interface CreateMessageFieldProps {
  authoringState: TextComposerAuthoringState;
  onBodyTextChange: (bodyText: string) => void;
  primaryChannel: TextComposerDerivedOutput["primaryChannel"];
  fallbackChannel: TextComposerDerivedOutput["fallbackChannel"];
  previewRecipientViewMode: "primary" | "fallback";
  onPreviewRecipientViewModeChange: (mode: "primary" | "fallback") => void;
  transportCountLabel: string;
  messageStatsTooltip: string;
  characterCount: number;
  isNearCharacterLimit: boolean;
  characterLimit: number;
  showCharacterCountApproximation?: boolean;
  /** Drop the outer elevation-0 shell when the caller already provides a
      surrounding container (e.g. the BroadcastV2 SMS flow without A/B). */
  hideShell?: boolean;
  /** Hide the shell's "Create message" heading while keeping the shell — e.g.
      the BroadcastV2 SMS flow, which already has its own section heading. */
  hideTitle?: boolean;
  hideChannelPicker?: boolean;
  disabled?: boolean;
  isFallbackUnlinked?: boolean;
  onUnlinkFallback?: () => void;
  onRelinkFallback?: () => void;
}

/** Lexical config for a composer editor: extension nodes + shared theme. */
export function buildComposerLexicalConfig(
  namespace: string,
  extensions: TextComposerExtension[],
) {
  return {
    namespace,
    nodes: extensions.flatMap((ext) => ext.editor?.nodes ?? []),
    onError: (error: Error) => {
      console.error(`${namespace} error:`, error);
    },
    theme: {
      paragraph: "lexical-paragraph",
    },
  };
}

const CHARACTER_COUNT_APPROXIMATION_TOOLTIP =
  "If merge tags or unique offers are used, they will be replaced with real content at send time and may increase the final character count.";

export function ExtensionSlots({
  extensions,
  authoringState,
  disabled,
  activeChannel,
}: {
  extensions: TextComposerExtension[];
  authoringState: TextComposerAuthoringState;
  disabled: boolean;
  activeChannel: TextComposerDerivedOutput["primaryChannel"] | null;
}) {
  return (
    <>
      {extensions.map((ext) => {
        if (!ext.component) return null;
        const Component = ext.component;
        return (
          <Component
            key={ext.id}
            authoringState={authoringState}
            disabled={disabled}
            activeChannel={activeChannel ?? undefined}
          />
        );
      })}
    </>
  );
}

function ExtensionToolbarItems({
  extensions,
  disabled,
}: {
  extensions: TextComposerExtension[];
  disabled: boolean;
}) {
  return (
    <>
      {extensions.flatMap((ext, extIndex) =>
        (ext.editor?.toolbarItems ?? []).map((Item, itemIndex) => (
          <Item
            key={`${ext.id}-${extIndex}-${itemIndex}`}
            disabled={disabled}
          />
        )),
      )}
    </>
  );
}

const relinkWarning = (fallbackChannel: string) =>
  `The ${fallbackChannel} version will match your RCS message again. Your custom changes will be lost.`;

const unlinkWarning = (fallbackChannel: string) =>
  `The ${fallbackChannel} message will stop matching your RCS message so you can customize it separately.`;

const LINK_ICON_SX = {
  fontSize: (theme: Theme) => theme.typography.fontSizeScale["0.875rem"],
} as const;

function FallbackLinkToggle({
  fallbackChannel,
  isFallbackUnlinked,
  onUnlinkFallback,
  onRelinkFallback,
  disabled,
}: {
  fallbackChannel: NonNullable<TextComposerDerivedOutput["fallbackChannel"]>;
  isFallbackUnlinked: boolean;
  onUnlinkFallback: () => void;
  onRelinkFallback: () => void;
  disabled: boolean;
}) {
  const [isRelinkConfirmOpen, setIsRelinkConfirmOpen] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

  return (
    <>
      <Tooltip
        placement="top"
        title={
          isFallbackUnlinked
            ? "Match the RCS message again"
            : `Customize the ${fallbackChannel} message separately`
        }
      >
        <IconButton
          aria-label={
            isFallbackUnlinked ? "Match text to RCS" : "Customize text fallback"
          }
          disabled={disabled}
          sx={(theme) => ({
            width: theme.spacing(6),
            height: theme.spacing(6),
            padding: 0,
            marginX: theme.spacing(2),
          })}
          onClick={(event) => {
            event.stopPropagation();
            if (isFallbackUnlinked) {
              setIsRelinkConfirmOpen(true);
            } else {
              setIsUnlinkConfirmOpen(true);
            }
          }}
        >
          {isFallbackUnlinked ? (
            <LinkOffIcon sx={LINK_ICON_SX} />
          ) : (
            <LinkIcon sx={LINK_ICON_SX} />
          )}
        </IconButton>
      </Tooltip>
      <DeleteConfirmationModal
        open={isRelinkConfirmOpen}
        isPending={false}
        title={`Match the ${fallbackChannel} message to RCS?`}
        confirmLabel="Match to RCS"
        contentText={relinkWarning(fallbackChannel)}
        onClose={() => setIsRelinkConfirmOpen(false)}
        onConfirm={() => {
          setIsRelinkConfirmOpen(false);
          onRelinkFallback();
        }}
      />
      <DeleteConfirmationModal
        open={isUnlinkConfirmOpen}
        isPending={false}
        title={`Customize the ${fallbackChannel} message separately?`}
        confirmLabel="Customize"
        contentText={unlinkWarning(fallbackChannel)}
        onClose={() => setIsUnlinkConfirmOpen(false)}
        onConfirm={() => {
          setIsUnlinkConfirmOpen(false);
          onUnlinkFallback();
        }}
      />
    </>
  );
}

function ComposeChannelPicker({
  primaryChannel,
  fallbackChannel,
  previewRecipientViewMode,
  onPreviewRecipientViewModeChange,
  isFallbackUnlinked = false,
  onUnlinkFallback,
  onRelinkFallback,
  disabled,
}: {
  primaryChannel: TextComposerDerivedOutput["primaryChannel"];
  fallbackChannel: TextComposerDerivedOutput["fallbackChannel"];
  previewRecipientViewMode: "primary" | "fallback";
  onPreviewRecipientViewModeChange: (mode: "primary" | "fallback") => void;
  isFallbackUnlinked?: boolean;
  onUnlinkFallback?: () => void;
  onRelinkFallback?: () => void;
  disabled: boolean;
}) {
  const theme = useTheme();
  const hasFallback = fallbackChannel !== null;
  const activeChannel =
    previewRecipientViewMode === "fallback" && hasFallback
      ? fallbackChannel
      : primaryChannel;
  const canToggleLink =
    onUnlinkFallback !== undefined && onRelinkFallback !== undefined;

  if (hasFallback) {
    return (
      <RecipientTopBarToggle sx={{ marginLeft: 0 }}>
        <RecipientTopBarToggleButton
          active={previewRecipientViewMode === "primary"}
          onClick={() => {
            if (!disabled) {
              onPreviewRecipientViewModeChange("primary");
            }
          }}
          sx={{
            backgroundColor:
              previewRecipientViewMode === "primary"
                ? getChannelColor(theme, primaryChannel)
                : "transparent",
            ...(disabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
          }}
        >
          <Typography
            variant="caption"
            component="span"
            sx={(t) => ({
              color:
                previewRecipientViewMode === "primary"
                  ? t.palette.primary.contrastText
                  : t.palette.text.secondary,
            })}
          >
            {primaryChannel}
          </Typography>
        </RecipientTopBarToggleButton>
        {canToggleLink ? (
          <FallbackLinkToggle
            fallbackChannel={fallbackChannel}
            isFallbackUnlinked={isFallbackUnlinked}
            onUnlinkFallback={onUnlinkFallback}
            onRelinkFallback={onRelinkFallback}
            disabled={disabled}
          />
        ) : null}
        <RecipientTopBarToggleButton
          active={previewRecipientViewMode === "fallback"}
          onClick={() => {
            if (!disabled) {
              onPreviewRecipientViewModeChange("fallback");
            }
          }}
          sx={{
            backgroundColor:
              previewRecipientViewMode === "fallback"
                ? getChannelColor(theme, fallbackChannel)
                : "transparent",
            ...(disabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
          }}
        >
          <Typography
            variant="caption"
            component="span"
            sx={(t) => ({
              color:
                previewRecipientViewMode === "fallback"
                  ? t.palette.primary.contrastText
                  : t.palette.text.secondary,
            })}
          >
            {fallbackChannel}
          </Typography>
        </RecipientTopBarToggleButton>
      </RecipientTopBarToggle>
    );
  }

  return (
    <RecipientTopBarChannelBadge
      sx={{
        backgroundColor: getChannelColor(theme, activeChannel),
        ...(disabled ? { opacity: 0.5 } : {}),
      }}
    >
      <Typography variant="caption" component="span" color="inherit">
        {activeChannel}
      </Typography>
    </RecipientTopBarChannelBadge>
  );
}

export function CreateMessageField({
  authoringState,
  onBodyTextChange,
  primaryChannel,
  fallbackChannel,
  previewRecipientViewMode,
  onPreviewRecipientViewModeChange,
  transportCountLabel,
  messageStatsTooltip,
  characterCount,
  isNearCharacterLimit,
  characterLimit,
  showCharacterCountApproximation = false,
  hideShell = false,
  hideTitle = false,
  hideChannelPicker = false,
  disabled = false,
  isFallbackUnlinked,
  onUnlinkFallback,
  onRelinkFallback,
}: CreateMessageFieldProps) {
  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [mediaPreview, setMediaPreview] = useState<MessageDraftMedia | null>(
    null,
  );
  const extensions = useMessageDraftExtensions();
  const storeId = useMessageDraftStoreId();
  const removeMedia = useMessageDraftStore((s) => s.actions.removeMedia) as
    | ((index: number) => void)
    | undefined;
  const isEmojiPickerOpen = emojiPickerAnchorEl !== null;

  const lexicalConfig = useMemo(
    () => buildComposerLexicalConfig("TextComposerEditor", extensions),
    [extensions],
  );

  const handleOpenEmojiPicker = (event: MouseEvent<HTMLButtonElement>) => {
    setEmojiPickerAnchorEl(event.currentTarget);
  };

  const handleCloseEmojiPicker = () => {
    setEmojiPickerAnchorEl(null);
  };

  const handleCloseMediaPreview = () => {
    setMediaPreview(null);
  };

  const activeChannel =
    previewRecipientViewMode === "fallback" && fallbackChannel !== null
      ? fallbackChannel
      : primaryChannel;

  const editorContent = (
    <>
      <SurfaceCard>
        <CreateMessageStack>
          <Typography variant="body2" color="text.secondary">
            Message
          </Typography>

          <MessageInputContainer>
            {authoringState.media?.map((media, mediaIndex) =>
              isDocumentMedia(media.mimeType) ? (
                <AttachedDocumentItem key={`${media.name}-${mediaIndex}`}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 14,
                      bgcolor: "#c9ccd2",
                    }}
                  >
                    {vcardDisplayName(media.name).charAt(0).toUpperCase() ||
                      "?"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle3" noWrap>
                      {vcardDisplayName(media.name)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Contact card
                    </Typography>
                  </Box>
                  <RemoveMediaButton
                    aria-label={`Remove ${media.name}`}
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeMedia?.(mediaIndex);
                    }}
                  >
                    &#10005;
                  </RemoveMediaButton>
                </AttachedDocumentItem>
              ) : (
                <AttachedMediaItem key={`${media.name}-${mediaIndex}`}>
                  <AttachedMediaPreview
                    src={media.cdnUrl}
                    alt={disabled ? media.name : ""}
                    role={disabled ? undefined : "button"}
                    tabIndex={disabled ? undefined : 0}
                    aria-label={disabled ? undefined : `Preview ${media.name}`}
                    sx={disabled ? undefined : { cursor: "pointer" }}
                    onClick={
                      disabled
                        ? undefined
                        : () => {
                            setMediaPreview(media);
                          }
                    }
                    onKeyDown={
                      disabled
                        ? undefined
                        : (event: KeyboardEvent<HTMLImageElement>) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setMediaPreview(media);
                            }
                          }
                    }
                  />
                  <RemoveMediaButton
                    aria-label={`Remove ${media.name}`}
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeMedia?.(mediaIndex);
                    }}
                  >
                    &#10005;
                  </RemoveMediaButton>
                </AttachedMediaItem>
              ),
            )}
            <MessageEditorScrollArea>
              <LexicalEditorContent
                initialText={authoringState.bodyText ?? ""}
                onTextChange={onBodyTextChange}
                disabled={disabled}
                placeholder="Type your message"
                onCloseEmojiPicker={handleCloseEmojiPicker}
              />
            </MessageEditorScrollArea>
            <SmsStatsBar>
              <StatsMetaGroup aria-label="message transport stats">
                {!hideChannelPicker && (
                  <ComposeChannelPicker
                    primaryChannel={primaryChannel}
                    fallbackChannel={fallbackChannel}
                    previewRecipientViewMode={previewRecipientViewMode}
                    onPreviewRecipientViewModeChange={
                      onPreviewRecipientViewModeChange
                    }
                    isFallbackUnlinked={isFallbackUnlinked}
                    onUnlinkFallback={onUnlinkFallback}
                    onRelinkFallback={onRelinkFallback}
                    disabled={disabled}
                  />
                )}
                <Tooltip
                  title={messageStatsTooltip}
                  slotProps={{
                    tooltip: {
                      sx: {
                        whiteSpace: "pre-line",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <SmsSegmentCountText
                      variant="body2"
                      aria-label="segment count"
                    >
                      {transportCountLabel}
                    </SmsSegmentCountText>
                    <Divider orientation="vertical" flexItem />
                    <CharacterCountText
                      variant="body2"
                      nearLimit={isNearCharacterLimit}
                      aria-label="character count"
                    >
                      {`${characterCount}/${characterLimit}`}
                    </CharacterCountText>
                  </Box>
                </Tooltip>
                {showCharacterCountApproximation ? (
                  <Tooltip title={CHARACTER_COUNT_APPROXIMATION_TOOLTIP}>
                    <Box
                      component="span"
                      sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                      <InfoOutlined
                        fontSize="small"
                        color="action"
                        aria-label="Character count is approximate"
                      />
                    </Box>
                  </Tooltip>
                ) : null}
              </StatsMetaGroup>
              <StatsActionsGroup>
                <ExtensionToolbarItems
                  extensions={extensions}
                  disabled={disabled}
                />
                <EmojiButton
                  disabled={disabled}
                  isOpen={isEmojiPickerOpen}
                  anchorEl={emojiPickerAnchorEl}
                  onOpen={handleOpenEmojiPicker}
                  onClose={handleCloseEmojiPicker}
                />
              </StatsActionsGroup>
            </SmsStatsBar>
          </MessageInputContainer>
        </CreateMessageStack>
      </SurfaceCard>
      {!disabled && (
        <ExtensionSlots
          extensions={extensions}
          authoringState={authoringState}
          disabled={disabled}
          activeChannel={activeChannel}
        />
      )}
    </>
  );

  return (
    <LexicalComposer
      key={storeId}
      initialConfig={{ ...lexicalConfig, editable: !disabled }}
    >
      {hideShell ? (
        <CreateMessageStack>{editorContent}</CreateMessageStack>
      ) : (
        <CreateMessageShell>
          <CreateMessageStack>
            {!hideTitle && (
              <Typography variant="subtitle1" color="text.primary">
                Create message
              </Typography>
            )}
            {editorContent}
          </CreateMessageStack>
        </CreateMessageShell>
      )}
      <Dialog
        open={mediaPreview !== null}
        onClose={handleCloseMediaPreview}
        maxWidth="lg"
        fullWidth
        aria-labelledby="attached-media-preview-title"
      >
        {mediaPreview ? (
          <>
            <DialogTitle
              id="attached-media-preview-title"
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                p: 0,
                backgroundColor: theme.palette.background["paper-elevation-1"],
              })}
            >
              <Box
                component="img"
                src={HEX_BAR_URL}
                alt=""
                sx={{
                  display: "block",
                  width: "100%",
                  height: "8px",
                  objectFit: "cover",
                }}
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={(theme) => ({
                  px: theme.spacing(3),
                  py: theme.spacing(3),
                  minWidth: 0,
                })}
              >
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {mediaPreview.name}
                </Typography>
                <Button
                  type="button"
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={handleCloseMediaPreview}
                  aria-label="Close preview"
                  sx={{ flexShrink: 0, textTransform: "none" }}
                >
                  × Close
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                pt: 2,
              }}
            >
              {isVideoMimeType(mediaPreview.mimeType) ? (
                <video
                  controls
                  playsInline
                  src={mediaPreview.cdnUrl}
                  style={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              ) : (
                <img
                  src={mediaPreview.cdnUrl}
                  alt={mediaPreview.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              )}
            </DialogContent>
          </>
        ) : null}
      </Dialog>
    </LexicalComposer>
  );
}

function EmojiButton({
  disabled,
  isOpen,
  anchorEl,
  onOpen,
  onClose,
}: {
  disabled: boolean;
  isOpen: boolean;
  anchorEl: HTMLButtonElement | null;
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
}) {
  const theme = useTheme();

  return (
    <>
      <Tooltip title="Insert emoji">
        <span>
          <IconButton
            aria-label="Insert emoji"
            onClick={onOpen}
            disabled={disabled}
            sx={{
              color: theme.palette.text.primary,
              width: 24,
              height: 24,
            }}
          >
            <MoodIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <EmojiPickerPopoverContainer>
          <EmojiPickerWithLexical onClose={onClose} />
        </EmojiPickerPopoverContainer>
      </Popover>
    </>
  );
}

function EmojiPickerWithLexical({ onClose }: { onClose: () => void }) {
  const [editor] = useLexicalComposerContext();

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    editor.dispatchCommand(INSERT_TEXT_AT_CURSOR_COMMAND, emojiData.emoji);
    onClose();
    requestAnimationFrame(() => {
      editor.focus();
    });
  };

  return (
    <EmojiPicker
      onEmojiClick={handleEmojiClick}
      skinTonesDisabled
      previewConfig={{ showPreview: false }}
    />
  );
}
